import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { getApiUrl } from "@/utils/api";
import { useAuth } from "@/hooks/useAuth";
import { companyAtom } from "@/store/company";
import { CO2SummaryResponse, Company } from "@/types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { Flame, Filter, TrendingDown, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DashboardScopeCard } from "@/components/dashboard/DashboardScopeCard";
import { DashboardCo2Data } from "@/components/dashboard/DashboardCo2Data";

// Types for the CO2 Summary API response

export default function Dashboard() {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [company, setCompany] = useAtom(companyAtom);
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear(),
  );
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [didAutoFetch, setDidAutoFetch] = useState<boolean>(false);

  // Generate year options (10 years before and after current year)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from(
    { length: 21 },
    (_, i) => currentYear - 10 + i,
  );

  // Fetch companies
  const { data: companiesData = { results: [] } } = useQuery<{
    results: Company[];
  }>({
    queryKey: ["companies"],
    queryFn: async () => {
      try {
        const url = getApiUrl("companies/");
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch companies: ${response.status}`);
        }

        const data = await response.json();
        return data;
      } catch (error) {
        console.error("Error fetching companies:", error);
        return { results: [] };
      }
    },
    enabled: !!token,
  });

  // Fetch CO2 summary data
  const {
    data: co2Data,
    isLoading: isLoadingCO2,
    refetch,
  } = useQuery<CO2SummaryResponse>({
    queryKey: ["co2-summary", selectedCompany?.id, selectedYear],
    queryFn: async () => {
      if (!selectedCompany?.id) throw new Error("No company selected");

      try {
        const url = getApiUrl(
          `companies/${selectedCompany.id}/co2-summary/?year=${selectedYear}`,
        );
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch CO2 summary: ${response.status}`);
        }

        const data = await response.json();
        return data;
      } catch (error) {
        console.error("Error fetching CO2 summary:", error);
        throw error;
      }
    },
    enabled: false,
    gcTime: 0,
    staleTime: 0,
    refetchOnMount: "always",
  });

  useEffect(() => {
    if (companiesData?.results?.length && !company) {
      const firstCompany = companiesData.results[0];
      setCompany(firstCompany);
      if (companiesData.results.length > 0) {
        setSelectedCompany(firstCompany);
      }
    }
  }, [companiesData, company, setCompany]);

  // Ensure the local selectedCompany is initialized and stays in sync
  // with the persistent companyAtom. Default to the first company.
  useEffect(() => {
    if (company) {
      // If there is a company in the atom, reflect it locally
      setSelectedCompany(company);
    } else if (companiesData?.results?.length) {
      // If no atom company yet, default locally to the first company
      setSelectedCompany(companiesData.results[0] as Company);
    }
  }, [company, companiesData]);

  // Auto-execute the fetch once after defaults are populated
  useEffect(() => {
    if (!didAutoFetch && selectedCompany?.id && selectedYear) {
      refetch();
      setHasSearched(true);
      setDidAutoFetch(true);
    }
  }, [didAutoFetch, selectedCompany, selectedYear, refetch]);

  // Handle company change
  const handleCompanyChange = (companyId: string) => {
    const selected = companiesData?.results?.find(
      (c) => c.id.toString() === companyId,
    );
    if (selected) {
      setSelectedCompany(selected);
      // Persist selection to the global company atom so the rest of the app stays in sync
      setCompany(selected);
      setHasSearched(false);
    }
  };

  // Handle year change
  const handleYearChange = (value: string) => {
    setSelectedYear(parseInt(value));
    setHasSearched(false);
  };

  // Calculate scope totals
  const getScopeData = () => {
    if (!co2Data) return { scope1: 0, scope2: 0, scope3: 0 };

    const { breakdown } = co2Data;
    const scope1 =
      breakdown.combustion.co2_eq +
      breakdown.consumption.co2_eq +
      breakdown.emission.co2_eq;
    const scope2 = breakdown.electricity.co2_eq;
    const scope3 = 0; // Based on the API response, scope 3 seems to be separate or not included

    return { scope1, scope2, scope3 };
  };

  const scopeData = getScopeData();
  const totalCO2 = co2Data?.total_co2_eq || 0;

  // Calculate percentages for the circular chart
  const scope1Percentage =
    totalCO2 > 0 ? (scopeData.scope1 / totalCO2) * 100 : 0;
  const scope2Percentage =
    totalCO2 > 0 ? (scopeData.scope2 / totalCO2) * 100 : 0;
  // const scope3Percentage =
  //   totalCO2 > 0 ? (scopeData.scope3 / totalCO2) * 100 : 0;

  // Circular chart geometry (single ring, sequence arcs to avoid overlap)
  const R = 40; // ring radius for all scopes
  const C = 2 * Math.PI * R;
  const STROKE = 10; // thicker stroke to match sample style
  const START_DEG = 45; // shift arcs ~45 degrees clockwise from the top (top-right like sample)
  const START = (START_DEG / 360) * C;
  const OVERLAP = STROKE; // strong overlap equal to stroke width to mask any sliver

  // Effective visible arc lengths after reserving caps at both ends
  let L1 = (scope1Percentage / 100) * C;
  let L2 = (scope2Percentage / 100) * C;
  // Normalize so the two arcs cover the full circle (no background showing)
  const totalArc = L1 + L2;
  if (totalArc < C) {
    L1 = Math.min(C, L1 + (C - totalArc));
  } else if (totalArc > C) {
    const scale = C / totalArc;
    L1 *= scale;
    L2 *= scale;
  }

  // Slightly extend yellow arc to fight anti-aliasing seams
  const A2 = Math.min(C, L2 + STROKE * 0.5);

  const handleFilterClick = () => {
    refetch();
    setHasSearched(true);
  };

  console.log("Dashboard.tsx", co2Data);

  return (
    <div className="p-6 space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{selectedCompany?.name || "..."}</h1>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 py-2 px-4 rounded-lg">
        {/* Company Selector */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{t("dashboard.company")}:</span>
          <Select
            value={selectedCompany?.id?.toString()}
            onValueChange={handleCompanyChange}
          >
            <SelectTrigger className="w-64 rounded-full">
              <SelectValue placeholder="Select company" />
            </SelectTrigger>
            <SelectContent>
              {companiesData?.results?.map((company) => (
                <SelectItem key={company.id} value={company.id.toString()}>
                  {company.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {/* Year Selector */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{t("dashboard.year")}:</span>
          <Select
            value={selectedYear.toString()}
            onValueChange={handleYearChange}
          >
            <SelectTrigger className="w-32 rounded-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {/* Filter Button */}
        <div
          role="button"
          size="icon"
          disabled={!company?.id || isLoadingCO2}
          onClick={handleFilterClick}
          className="cursor-pointer flex items-center justify-center px-8 py-2 rounded-full bg-[#A3F800] hover:bg-[#e5e5e5]"
        >
          <Filter size={24} fill="#000" />
        </div>
      </div>

      {/* Main Content */}
      {co2Data && hasSearched && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          {/* Left Side - Circular Chart */}
          <Card>
            <CardTitle className="pt-4 pb-0 px-4 font-semibold text-[18px]">
              {t("dashboard.pieTitle")}
            </CardTitle>
            <CardContent className="p-6">
              <div className="w-full grid grid-cols-2 items-end justify-center gap-4">
                {/* Chart */}
                <div className=" flex items-center justify-center">
                  <div className="relative w-64 h-64">
                    {/* Circular Progress Chart */}
                    <svg
                      className="w-full h-full transform -rotate-90"
                      viewBox="0 0 100 100"
                    >
                      {/* Base full ring in green to eliminate any background sliver (slightly thicker for underpaint) */}
                      <circle
                        cx="50"
                        cy="50"
                        r={R}
                        fill="none"
                        stroke="#5ea500"
                        strokeWidth={STROKE}
                        strokeLinecap="butt"
                      />
                      {/* Yellow portion overlay */}
                      <circle
                        cx="50"
                        cy="50"
                        r={R}
                        fill="none"
                        stroke="#9ae600"
                        strokeWidth={STROKE}
                        strokeLinecap="round"
                        strokeDasharray={`${A2} ${C}`}
                        strokeDashoffset={`${-(Math.max(0, L1 - OVERLAP) + START)}`}
                      />
                    </svg>

                    {/* Center Text */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <div className="text-[40px] font-extrabold">
                        {(totalCO2 / 1000).toFixed(2)}
                      </div>
                      <div className="text-[18px] font-bold -mt-3">
                        ton. CO2 eq.
                      </div>
                    </div>
                  </div>
                </div>
                {/* Legend */}
                <div className="space-y-2 pb-16">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-lime-600 rounded-full"></div>
                    <div className="flex items-baseline space-x-1 text-[14px] font-bold">
                      <span className="font-normal">
                        {t("common.level")} 1:
                      </span>
                      {/*<span>{(scopeData.scope1 / 1000).toFixed(2)}</span>
                      <span>ton CO2 eq.</span>*/}
                      <span>{scope1Percentage.toFixed(0)}%</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-lime-400 rounded-full"></div>
                    <div className="flex items-baseline space-x-1 text-[14px] font-bold">
                      <span className="font-normal">
                        {t("common.level")} 2:
                      </span>
                      {/*<span>{(scopeData.scope2 / 1000).toFixed(2)}</span>
                      <span>ton CO2 eq.</span>*/}
                      <span>{scope2Percentage.toFixed(0)}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Right Side - Scope Breakdown */}
          <Card>
            <CardTitle className="pt-4 pb-0 px-4 font-semibold text-[18px]">
              {t("dashboard.summaryTitle")}
            </CardTitle>
            <CardContent className="h-[calc(100%-100px)] flex flex-col justify-end px-6 space-y-4">
              {/* Scope 1 */}
              <DashboardScopeCard
                scope={1}
                value={scopeData.scope1}
                icon={<Flame size={48} />}
                bg="bg-lime-600"
                categories={["combustion", "consumption", "recharges"]}
              />

              {/* Scope 2 */}
              <DashboardScopeCard
                scope={2}
                value={scopeData.scope2}
                icon={<Zap size={48} />}
                bg="bg-lime-400"
                categories={["electricity"]}
              />

              {/* Scope 3 */}
              {/* <DashboardScopeCard
                scope={3}
                value={scopeData.scope3}
                icon={<Droplets size={48} />}
                bg="bg-lime-200"
              /> */}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Environmental Impact by Categories */}
      {co2Data && hasSearched && <DashboardCo2Data co2Data={co2Data} />}

      {/* No Data State */}
      {!co2Data && !isLoadingCO2 && !hasSearched && (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="text-gray-500">
              <TrendingDown className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-semibold mb-2">
                {t("dashboard.selectcompany")}
              </h3>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
